import { useCallback, useEffect, useState } from 'react'
import { adminSessionStore, errorMessage, getUser, listAttempts, listUsers, setUserActive } from '../../services/admin'
import { faDigits } from '../../lib/numerals'
import { LEVEL_FA } from './constants'
import { formatDate, formatDatetime, formatDayCompact } from './dates'

// ─── Users tab (admin-users, S3-p) ────────────────────────────────────────────

const USER_STATUS_FA = { active: 'فعال', inactive: 'غیرفعال' }
const USER_STATUS_CLS = { active: 'bg-green-100 text-green-800', inactive: 'bg-red-100 text-red-700' }
const ROLE_FA = { learner: 'زبان‌آموز', admin: 'ادمین' }
const ATTEMPT_STATUS_FA = { pending: 'در انتظار', analyzing: 'در حال تحلیل', scored: 'امتیازدهی‌شده', failed: 'ناموفق' }

/** The wire's 3–500 character rule for a set-active reason, checked before the request so the admin gets the words, not a 422. */
export const REASON_MIN = 3
export const REASON_MAX = 500

/** How the panel explains a 403 `admin_jwt_required` — the shared key may read, only a phone-signed-in admin may write. */
export const JWT_REQUIRED_FA = 'این کار فقط با ورود از طریق شماره تلفن ممکن است؛ کلید مشترک ادمین مجاز نیست. از پنل خارج شوید و با شماره خود وارد شوید.'

const inputCls = 'rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none'
const btnPrimary = 'rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50'
const btnGhost = 'rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50'

export function UserStatusBadge({ status }) {
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${USER_STATUS_CLS[status] ?? 'bg-slate-100 text-slate-500'}`}>
      {USER_STATUS_FA[status] ?? status}
    </span>
  )
}

function NameCell({ user }) {
  if (!user.fullName && !user.displayName) return <span className="text-slate-400">—</span>
  return (
    <>
      <div className="font-medium text-slate-700">{user.fullName ?? user.displayName}</div>
      {user.fullName && user.displayName && <div className="text-xs text-slate-400" dir="ltr">{user.displayName}</div>}
    </>
  )
}

/**
 * The list, given its rows. Every row shows `phoneMasked` and nothing else
 * about the number: the backend never puts the full phone in a list, and
 * this table has no column that could.
 */
export function UsersTable({ items, onSelect }) {
  if (items.length === 0) return <p className="py-16 text-center text-sm text-slate-400">کاربری یافت نشد.</p>
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-xs text-slate-500">
            <th className="px-4 py-2 text-start">شماره</th>
            <th className="px-4 py-2 text-start">نام</th>
            <th className="px-4 py-2 text-start">سطح</th>
            <th className="px-4 py-2 text-start">وضعیت</th>
            <th className="px-4 py-2 text-start">ثبت‌نام</th>
            <th className="px-4 py-2 text-start">آخرین تلاش</th>
            <th className="px-4 py-2 text-start"></th>
          </tr>
        </thead>
        <tbody>
          {items.map(u => (
            <tr key={u.id} className="border-t border-slate-50 align-top">
              <td className="whitespace-nowrap px-4 py-2 text-xs text-slate-700" dir="ltr">{u.phoneMasked}</td>
              <td className="px-4 py-2"><NameCell user={u} /></td>
              <td className="px-4 py-2 text-xs text-slate-600">{LEVEL_FA[u.level] ?? u.level}{u.role === 'admin' && <span className="ms-1 text-slate-400">· ادمین</span>}</td>
              <td className="px-4 py-2"><UserStatusBadge status={u.status} /></td>
              <td className="whitespace-nowrap px-4 py-2 text-xs text-slate-500">{formatDate(u.createdAt)}</td>
              <td className="whitespace-nowrap px-4 py-2 text-xs text-slate-500">{formatDatetime(u.lastAttemptAt)}</td>
              <td className="px-4 py-2 text-end">
                <button type="button" onClick={() => onSelect(u.id)} className="text-xs font-medium text-brand-700 hover:underline">جزئیات</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** A learner's own attempts (admin-attempts_list with `userId`), newest first, compact. */
export function UserAttemptsTable({ attempts }) {
  if (attempts.length === 0) return <p className="py-6 text-center text-sm text-slate-400">هنوز تلاشی ثبت نشده.</p>
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-xs text-slate-500">
            <th className="px-4 py-2 text-start">زمان</th>
            <th className="px-4 py-2 text-start">درس / جمله</th>
            <th className="px-4 py-2 text-start">وضعیت</th>
            <th className="px-4 py-2 text-start">امتیاز کلی</th>
          </tr>
        </thead>
        <tbody>
          {attempts.map(a => (
            <tr key={a.id} className="border-t border-slate-50 align-top">
              <td className="whitespace-nowrap px-4 py-2 text-xs text-slate-500">{formatDatetime(a.createdAt)}</td>
              <td className="px-4 py-2 text-slate-700">
                <div className="font-medium">{a.lessonTitleEn}</div>
                <div className="text-xs text-slate-400">#{faDigits(a.sentenceIndex + 1)} — {a.sentenceTextEn}</div>
              </td>
              <td className="px-4 py-2 text-xs text-slate-600">{ATTEMPT_STATUS_FA[a.status] ?? a.status}</td>
              <td className="px-4 py-2 text-xs text-slate-600">
                {a.status === 'scored' ? faDigits(a.overall) : a.status === 'failed' ? <span className="text-red-600">{a.errorMessage ?? 'نامشخص'}</span> : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Activate / deactivate with a reason. The button does not act on the first
 * click: it opens an inline reason row (3–500 characters, the backend's own
 * rule) with confirm and cancel, so the account it is about stays on screen.
 * The shared admin key cannot do this at all (403 `admin_jwt_required`);
 * when the session has no JWT the row says so up front and the server's
 * refusal, should it still come, is shown in the same words.
 */
export function SetActiveForm({ user, jwtSignedIn, onChanged }) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [done, setDone] = useState('')

  const deactivating = user.status === 'active'
  const trimmed = reason.trim()
  const reasonOk = trimmed.length >= REASON_MIN && trimmed.length <= REASON_MAX

  async function submit(e) {
    e.preventDefault()
    if (!reasonOk) { setErr(`دلیل باید بین ${faDigits(REASON_MIN)} تا ${faDigits(REASON_MAX)} نویسه باشد.`); return }
    setBusy(true); setErr(''); setDone('')
    try {
      const result = await setUserActive(user.id, !deactivating, trimmed)
      setOpen(false); setReason('')
      setDone(result.changed ? (result.status === 'active' ? 'حساب فعال شد.' : 'حساب غیرفعال شد.') : 'حساب از قبل در همین وضعیت بود؛ چیزی ثبت نشد.')
      onChanged(result)
    } catch (ex) {
      const code = ex?.response?.data?.error
      if (ex?.response?.status === 403 && code === 'admin_jwt_required') setErr(JWT_REQUIRED_FA)
      else if (code === 'cannot_deactivate_self') setErr('حساب خودتان را نمی‌توانید غیرفعال کنید.')
      else setErr(errorMessage(ex))
    } finally { setBusy(false) }
  }

  return (
    <form onSubmit={submit} className="space-y-2">
      {!open && (
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={() => { setOpen(true); setErr(''); setDone('') }}
            className={deactivating
              ? 'rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700'
              : btnPrimary}>
            {deactivating ? 'غیرفعال کردن حساب' : 'فعال کردن حساب'}
          </button>
          {!jwtSignedIn && (
            <span className="text-xs text-amber-700">با کلید مشترک وارد شده‌اید؛ تغییر وضعیت فقط با ورود از طریق شماره تلفن ممکن است.</span>
          )}
        </div>
      )}
      {open && (
        <div className={`space-y-2 rounded-lg px-3 py-3 text-sm ${deactivating ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-800'}`}>
          <p>
            {deactivating
              ? 'با غیرفعال شدن، این کاربر دیگر نمی‌تواند وارد شود و هر درخواستش رد می‌شود. دلیل در گزارش ممیزی ثبت می‌شود.'
              : 'حساب دوباره فعال می‌شود و کاربر می‌تواند وارد شود. دلیل در گزارش ممیزی ثبت می‌شود.'}
          </p>
          <label className="block">
            <span className="mb-1 block text-xs font-medium">دلیل (۳ تا ۵۰۰ نویسه)</span>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} maxLength={REASON_MAX}
              className={`w-full bg-white ${inputCls}`} placeholder="مثلاً: نظرهای توهین‌آمیز در فید، اخطار دوم" />
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <button type="submit" disabled={busy || !reasonOk}
              className={deactivating
                ? 'rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50'
                : 'rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50'}>
              {busy ? 'در حال ثبت…' : deactivating ? 'بله، غیرفعال کن' : 'بله، فعال کن'}
            </button>
            <button type="button" disabled={busy} onClick={() => { setOpen(false); setReason(''); setErr('') }} className={btnGhost}>انصراف</button>
            <span className="ms-auto text-xs opacity-70">{faDigits(trimmed.length)}/{faDigits(REASON_MAX)}</span>
          </div>
        </div>
      )}
      {err && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{err}</div>}
      {done && <div className="rounded-lg bg-green-50 px-4 py-2 text-sm text-green-700">{done}</div>}
    </form>
  )
}

function Field({ label, children, ltr }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-sm text-slate-800" dir={ltr ? 'ltr' : undefined}>{children}</div>
    </div>
  )
}

/**
 * One learner in full, given the detail row and their attempts. The full
 * phone is shown here — this is the one place the backend returns it.
 */
export function UserDetailView({ user, attempts, jwtSignedIn, onBack, onStatusChanged }) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={onBack} className={btnGhost}>← فهرست کاربران</button>
        <h2 className="text-base font-bold text-slate-800">{user.fullName ?? user.displayName ?? 'کاربر بدون نام'}</h2>
        <UserStatusBadge status={user.status} />
        {user.role === 'admin' && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">ادمین</span>}
      </div>

      <div className="grid grid-cols-2 gap-4 rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm sm:grid-cols-3 lg:grid-cols-4">
        <Field label="شماره تلفن" ltr>{user.phone}</Field>
        <Field label="نام نمایشی" ltr>{user.displayName ?? '—'}</Field>
        <Field label="ایمیل" ltr>{user.email ?? '—'}</Field>
        <Field label="تاریخ تولد">{user.birthDate ? formatDayCompact(user.birthDate) : '—'}</Field>
        <Field label="سطح">{LEVEL_FA[user.level] ?? user.level}</Field>
        <Field label="نقش">{ROLE_FA[user.role] ?? user.role}</Field>
        <Field label="امتیاز تجربه (XP)">{faDigits(user.xp)}</Field>
        <Field label="زنجیره (فعلی / بیشترین)">{faDigits(user.streakCurrent)} / {faDigits(user.streakLongest)}</Field>
        <Field label="تلاش‌ها (کل / امتیازدهی‌شده)">{faDigits(user.attemptCount)} / {faDigits(user.scoredAttemptCount)}</Field>
        <Field label="میانگین امتیاز">{user.averageScore == null ? '—' : faDigits(user.averageScore)}</Field>
        <Field label="آخرین تمرین">{user.lastPracticedOn ? formatDayCompact(user.lastPracticedOn) : '—'}</Field>
        <Field label="آخرین تلاش">{formatDatetime(user.lastAttemptAt)}</Field>
        <Field label="آخرین ورود">{formatDatetime(user.lastLoginAt)}</Field>
        <Field label="آخرین فعالیت">{formatDatetime(user.lastActivityAt)}</Field>
        <Field label="ثبت‌نام">{formatDatetime(user.createdAt)}</Field>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
        <h3 className="mb-2 text-sm font-semibold text-slate-700">وضعیت حساب</h3>
        <SetActiveForm user={user} jwtSignedIn={jwtSignedIn} onChanged={onStatusChanged} />
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-700">تلاش‌ها</h3>
        {attempts === null
          ? <p className="py-6 text-center text-sm text-slate-400">در حال بارگذاری…</p>
          : <UserAttemptsTable attempts={attempts} />}
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-700">اقدام‌های ادمین روی این حساب</h3>
        {user.auditTrail.length === 0
          ? <p className="py-6 text-center text-sm text-slate-400">هیچ اقدامی ثبت نشده.</p>
          : (
            <ul className="space-y-2">
              {user.auditTrail.map(entry => (
                <li key={entry.id} className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <code className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700" dir="ltr">{entry.action}</code>
                    <span className="text-xs text-slate-400">{formatDatetime(entry.createdAt)}</span>
                    {entry.actorId && <span className="text-xs text-slate-400" dir="ltr">admin {entry.actorId.slice(0, 8)}</span>}
                  </div>
                  <p className="mt-1 text-slate-700">{entry.reason}</p>
                </li>
              ))}
            </ul>
          )}
      </div>
    </div>
  )
}

/** Loads one learner and their attempts, then hands both to the view. */
function UserDetail({ userId, jwtSignedIn, onBack, onStatusChanged }) {
  const [user, setUser] = useState(null)
  const [attempts, setAttempts] = useState(null)
  const [err, setErr] = useState('')

  const load = useCallback(async () => {
    setErr('')
    try {
      setUser(await getUser(userId))
      setAttempts(await listAttempts({ userId, limit: 50 }))
    } catch (ex) { setErr(errorMessage(ex)) }
  }, [userId])

  useEffect(() => { load() }, [load])

  function changed(result) {
    onStatusChanged(result)
    // The audit trail has a new row now; re-read rather than guess its shape.
    if (result.changed) load()
  }

  if (err) {
    return (
      <div className="space-y-3">
        <button type="button" onClick={onBack} className={btnGhost}>← فهرست کاربران</button>
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{err}</div>
        <button type="button" onClick={load} className={btnGhost}>تلاش دوباره</button>
      </div>
    )
  }
  if (user === null) return <p className="py-16 text-center text-sm text-slate-400">در حال بارگذاری…</p>
  return <UserDetailView user={user} attempts={attempts} jwtSignedIn={jwtSignedIn} onBack={onBack} onStatusChanged={changed} />
}

/** The users tab (S3-p): search + status filter over `admin-users_list`, a detail view per learner. */
export function UsersTab() {
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [items, setItems] = useState(null)
  const [nextCursor, setNextCursor] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')
  const [selectedId, setSelectedId] = useState(null)

  const jwtSignedIn = Boolean(adminSessionStore.access)

  const load = useCallback(async (filters, append = false) => {
    setLoading(true); setErr('')
    try {
      const page = await listUsers(filters)
      setItems(prev => (append && prev ? [...prev, ...page.items] : page.items))
      setNextCursor(page.nextCursor)
    } catch (ex) { setErr(errorMessage(ex)) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load({}) }, [load])

  function filters() {
    return { ...(q.trim() ? { q: q.trim() } : {}), ...(status ? { status } : {}) }
  }

  function applyFilters(e) {
    e.preventDefault()
    load(filters())
  }

  function loadMore() {
    load({ ...filters(), cursor: nextCursor }, true)
  }

  function statusChanged(result) {
    setItems(list => list && list.map(u => u.id === result.id ? { ...u, status: result.status } : u))
  }

  if (selectedId) {
    return <UserDetail userId={selectedId} jwtSignedIn={jwtSignedIn} onBack={() => setSelectedId(null)} onStatusChanged={statusChanged} />
  }

  return (
    <div className="space-y-3">
      <form onSubmit={applyFilters} className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <label className="block min-w-48 flex-1">
          <span className="mb-1 block text-xs font-medium text-slate-600">جست‌وجو (شماره یا نام)</span>
          <input type="search" value={q} onChange={e => setQ(e.target.value)} placeholder="۰۹۱۲… یا نام"
            className={`w-full ${inputCls}`} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">وضعیت</span>
          <select value={status} onChange={e => setStatus(e.target.value)} className={inputCls}>
            <option value="">همه</option>
            <option value="active">فعال</option>
            <option value="inactive">غیرفعال</option>
          </select>
        </label>
        <button type="submit" disabled={loading} className={btnPrimary}>{loading ? '…' : 'اعمال فیلتر'}</button>
      </form>

      {err && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{err}</div>}

      {items && <UsersTable items={items} onSelect={setSelectedId} />}

      {items && nextCursor && (
        <div className="text-center">
          <button type="button" onClick={loadMore} disabled={loading} className={btnGhost}>{loading ? '…' : 'موارد بیشتر'}</button>
        </div>
      )}
    </div>
  )
}
